<template>
  <div class="container container-wide admin">
    <header class="admin__head reveal">
      <p class="eyebrow">Espace staff</p>
      <h1>🛡️ Modération</h1>
      <p class="admin__sub">Gère les signalements et garde la banane propre.</p>
    </header>

    <!-- Tabs -->
    <div class="tabs" role="tablist">
      <button
        v-for="t in tabs"
        :key="t.value"
        class="tabs__tab"
        :class="{ active: tab === t.value }"
        role="tab"
        :aria-selected="tab === t.value"
        @click="tab = t.value"
      >
        <span class="tabs__emoji">{{ t.emoji }}</span>{{ t.label }}
      </button>
    </div>

    <!-- Global toast -->
    <transition name="toast">
      <div
        v-if="toast"
        class="toast"
        :class="`toast--${toast.type}`"
        role="status"
      >
        {{ toast.text }}
        <button class="toast__close" aria-label="Fermer" @click="toast = null">✕</button>
      </div>
    </transition>

    <!-- ============ TAB 1 — REPORTED JOKES ============ -->
    <section v-show="tab === 'jokes'" class="panel">
      <p v-if="$apollo.queries.reportedJokes.loading" class="state">Chargement…</p>
      <p v-else-if="!reportedJokes.length" class="state state--empty">
        🍌 Rien à modérer ici. Tout est clean !
      </p>

      <article
        v-for="entry in reportedJokes"
        :key="entry.joke.id"
        class="mod-card"
      >
        <div class="mod-card__top">
          <span class="badge badge--count">🚩 {{ entry.reportCount }} signalement{{ entry.reportCount > 1 ? 's' : '' }}</span>
          <span class="mod-card__author">✍️ {{ entry.joke.authorName || 'Anonyme' }}</span>
          <span class="mod-card__id">#{{ entry.joke.id }}</span>
        </div>

        <p class="mod-card__text">{{ entry.joke.text }}</p>

        <ul class="reports">
          <li v-for="r in entry.reports" :key="r.id" class="report">
            <p class="report__msg">“{{ r.message }}”</p>
            <div v-if="r.proposedRewrite" class="report__rewrite">
              <span class="report__rewrite-tag">✏️ Réécriture proposée</span>
              <p>{{ r.proposedRewrite }}</p>
            </div>
          </li>
        </ul>

        <!-- Inline forms -->
        <div v-if="openForm(entry.joke.id, 'rewrite')" class="inline-form">
          <label class="inline-form__label">Nouveau texte</label>
          <textarea v-model="drafts[entry.joke.id].rewrite" rows="3" class="field"></textarea>
          <div class="inline-form__actions">
            <BananaButton
              size="sm"
              :loading="busyId === entry.joke.id"
              :disabled="!drafts[entry.joke.id].rewrite.trim()"
              @click="doRewrite(entry)"
            >Valider la réécriture</BananaButton>
            <BananaButton size="sm" variant="ghost" @click="closeForm(entry.joke.id)">Annuler</BananaButton>
          </div>
        </div>

        <div v-else-if="openForm(entry.joke.id, 'archive')" class="inline-form">
          <label class="inline-form__label">Motif d'archivage</label>
          <input v-model="drafts[entry.joke.id].reason" type="text" class="field" placeholder="Pourquoi archiver cette blague ?" />
          <div class="inline-form__actions">
            <BananaButton
              size="sm"
              variant="danger"
              :loading="busyId === entry.joke.id"
              :disabled="!drafts[entry.joke.id].reason.trim()"
              @click="doArchive(entry)"
            >Confirmer l'archivage</BananaButton>
            <BananaButton size="sm" variant="ghost" @click="closeForm(entry.joke.id)">Annuler</BananaButton>
          </div>
        </div>

        <div v-else class="mod-card__actions">
          <BananaButton size="sm" variant="outline" @click="startRewrite(entry)">✏️ Réécrire</BananaButton>
          <BananaButton size="sm" variant="danger" @click="startArchive(entry.joke.id)">🗄️ Archiver</BananaButton>
          <BananaButton size="sm" variant="ghost" :loading="busyId === entry.joke.id" @click="doDismiss(entry.joke.id)">✅ Ignorer les signalements</BananaButton>
        </div>
      </article>
    </section>

    <!-- ============ TAB 2 — REPORTED COMMENTS ============ -->
    <section v-show="tab === 'comments'" class="panel">
      <p v-if="$apollo.queries.reportedComments.loading" class="state">Chargement…</p>
      <p v-else-if="!reportedComments.length" class="state state--empty">
        💬 Aucun commentaire signalé.
      </p>

      <article
        v-for="entry in reportedComments"
        :key="entry.comment.id"
        class="mod-card"
      >
        <div class="mod-card__top">
          <span class="badge badge--count">🚩 {{ entry.reportCount }} signalement{{ entry.reportCount > 1 ? 's' : '' }}</span>
          <span v-if="entry.comment.isRemoved" class="badge badge--removed">Déjà supprimé</span>
          <span class="mod-card__id">#{{ entry.comment.id }}</span>
        </div>

        <p class="mod-card__text mod-card__text--comment">{{ entry.comment.text }}</p>

        <ul class="reports">
          <li v-for="r in entry.reports" :key="r.id" class="report">
            <p class="report__msg">“{{ r.message }}”</p>
          </li>
        </ul>

        <div v-if="openForm(entry.comment.id, 'remove')" class="inline-form">
          <label class="inline-form__label">Motif de suppression</label>
          <div class="reason-list">
            <button
              v-for="reason in commentReasons"
              :key="reason"
              type="button"
              class="reason-chip"
              :class="{ active: drafts[entry.comment.id].reasonChoice === reason }"
              @click="drafts[entry.comment.id].reasonChoice = reason"
            >{{ reason }}</button>
          </div>
          <input
            v-if="drafts[entry.comment.id].reasonChoice === 'Autre'"
            v-model="drafts[entry.comment.id].reasonCustom"
            type="text"
            class="field"
            placeholder="Précise le motif…"
          />
          <div class="inline-form__actions">
            <BananaButton
              size="sm"
              variant="danger"
              :loading="busyId === entry.comment.id"
              :disabled="!resolvedReason(entry.comment.id)"
              @click="doRemoveComment(entry)"
            >Supprimer le commentaire</BananaButton>
            <BananaButton size="sm" variant="ghost" @click="closeForm(entry.comment.id)">Annuler</BananaButton>
          </div>
        </div>

        <div v-else class="mod-card__actions">
          <BananaButton size="sm" variant="danger" @click="startRemove(entry.comment.id)">🗑️ Supprimer</BananaButton>
          <BananaButton size="sm" variant="ghost" :loading="busyId === entry.comment.id" @click="doDismissComment(entry.comment.id)">✅ Ignorer</BananaButton>
        </div>
      </article>
    </section>

    <!-- ============ TAB 3 — USERS DIRECTORY ============ -->
    <section v-show="tab === 'users'" class="panel">
      <!-- Search + summary bar -->
      <div class="users-bar">
        <div class="users-search">
          <span class="users-search__icon" aria-hidden="true">🔎</span>
          <input
            v-model="userSearch"
            type="search"
            class="users-search__field"
            placeholder="Chercher un email ou un nom…"
            @input="onSearchInput"
          />
        </div>
        <p v-if="!authError" class="users-count">
          {{ usersPage.totalCount }} compte{{ usersPage.totalCount > 1 ? 's' : '' }}
          <span v-if="userSearch.trim()"> trouvé{{ usersPage.totalCount > 1 ? 's' : '' }}</span>
        </p>
      </div>

      <div v-if="authError" class="state state--empty">
        <p>⏳ Ta session a expiré (ou tes droits ont changé).</p>
        <BananaButton variant="dark" size="sm" @click="reconnect">Se reconnecter</BananaButton>
      </div>
      <p v-else-if="$apollo.queries.usersPage.loading && !usersPage.items.length" class="state">Chargement…</p>
      <p v-else-if="!usersPage.items.length" class="state state--empty">
        🫥 Aucun compte ne correspond.
      </p>

      <div v-else class="users-table">
        <div class="users-row users-row--head">
          <button
            v-for="col in userColumns"
            :key="col.key"
            type="button"
            class="users-th"
            :class="{ 'users-th--active': sortColumn === col.key, 'users-th--sortable': col.sortable }"
            :disabled="!col.sortable"
            @click="col.sortable && toggleSort(col.key)"
          >
            {{ col.label }}
            <span v-if="col.sortable" class="users-th__arrow">{{ sortArrow(col.key) }}</span>
          </button>
        </div>
        <div v-for="u in usersPage.items" :key="u.id" class="users-row">
          <span class="users-name" data-label="Nom">{{ u.displayName || '—' }}</span>
          <span class="users-email" data-label="Email">{{ u.email }}</span>
          <span data-label="Inscrit le">{{ formatDate(u.dateJoined) }}</span>
          <span data-label="Rôle">
            <span v-if="u.isSuperuser" class="badge badge--super">👑 super-admin</span>
            <span v-else-if="u.isStaff" class="badge badge--staff">🛡️ admin</span>
            <span v-else class="badge badge--user">user</span>
          </span>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="!authError && usersPage.totalCount > userPageSize" class="users-pager">
        <BananaButton
          size="sm"
          variant="outline"
          :disabled="userOffset === 0"
          @click="prevPage"
        >← Précédents</BananaButton>
        <span class="users-pager__info">
          {{ userOffset + 1 }}–{{ Math.min(userOffset + userPageSize, usersPage.totalCount) }}
          / {{ usersPage.totalCount }}
        </span>
        <BananaButton
          size="sm"
          variant="outline"
          :disabled="userOffset + userPageSize >= usersPage.totalCount"
          @click="nextPage"
        >Suivants →</BananaButton>
      </div>
    </section>
  </div>
</template>

<script>
import BananaButton from '@/components/ui/BananaButton.vue'
import {
  REPORTED_JOKES_QUERY,
  REPORTED_COMMENTS_QUERY,
  REWRITE_JOKE_MUTATION,
  ARCHIVE_JOKE_MUTATION,
  REMOVE_COMMENT_MUTATION,
  DISMISS_REPORTS_MUTATION,
} from '@/graphql/moderation'
import { USERS_QUERY } from '@/graphql/auth'
import { logout } from '@/auth'

export default {
  name: 'AdminView',
  components: { BananaButton },
  data() {
    return {
      tab: 'jokes',
      tabs: [
        { value: 'jokes', label: 'Blagues signalées', emoji: '🚩' },
        { value: 'comments', label: 'Commentaires signalés', emoji: '💬' },
        { value: 'users', label: 'Utilisateurs', emoji: '👥' },
      ],
      reportedJokes: [],
      reportedComments: [],
      // Users directory (paginated back-office)
      usersPage: { items: [], totalCount: 0 },
      userColumns: [
        { key: 'NAME', label: 'Nom', sortable: true },
        { key: 'EMAIL', label: 'Email', sortable: true },
        { key: 'JOINED', label: 'Inscrit le', sortable: true },
        { key: 'ADMIN', label: 'Rôle', sortable: true },
      ],
      userSearch: '',
      userOffset: 0,
      userPageSize: 25,
      sortColumn: 'JOINED',
      sortDir: 'desc', // 'asc' | 'desc'
      searchDebounce: null,
      authError: false,
      commentReasons: [
        'Insultes / harcèlement',
        'Haine / discrimination',
        'Spam / hors-sujet',
        'Contenu illégal / choquant',
        'Autre',
      ],
      // per-entry UI state: { [id]: { mode, rewrite, reason, reasonChoice, reasonCustom } }
      drafts: {},
      busyId: null,
      toast: null,
      toastTimer: null,
    }
  },
  apollo: {
    reportedJokes: {
      query: REPORTED_JOKES_QUERY,
      variables: { count: 50, offset: 0 },
      fetchPolicy: 'cache-and-network',
      update: (data) => data.reportedJokes || [],
    },
    reportedComments: {
      query: REPORTED_COMMENTS_QUERY,
      variables: { count: 50, offset: 0 },
      fetchPolicy: 'cache-and-network',
      update: (data) => data.reportedComments || [],
    },
    usersPage: {
      query: USERS_QUERY,
      fetchPolicy: 'cache-and-network',
      variables() {
        return {
          count: this.userPageSize,
          offset: this.userOffset,
          search: this.userSearch.trim(),
          orderBy: this.orderByParam,
        }
      },
      update: (data) => data.users || { items: [], totalCount: 0 },
      result() {
        // A successful result clears any prior auth error.
        this.authError = false
      },
      error(err) {
        // A "Réservé aux modérateurs." error here almost always means the
        // session token expired (or the account lost staff rights) — surface
        // it as an auth problem instead of a misleading empty list.
        this.authError = /modérateur|reserv|forbidden|auth|token|credential/i.test(
          err?.graphQLErrors?.[0]?.message || err?.message || ''
        )
      },
    },
  },
  computed: {
    // Maps the (column, direction) UI state to the backend's orderBy enum.
    orderByParam() {
      const map = {
        NAME: { asc: 'NAME', desc: 'NAME_DESC' },
        EMAIL: { asc: 'EMAIL', desc: 'EMAIL_DESC' },
        JOINED: { asc: 'JOINED', desc: 'JOINED_DESC' },
        // "asc" = admins last, "desc" = admins first (most useful default view)
        ADMIN: { asc: 'ADMIN_LAST', desc: 'ADMIN_FIRST' },
      }
      return (map[this.sortColumn] || map.JOINED)[this.sortDir]
    },
  },
  methods: {
    // Session expired on a moderator query — log out and bounce to login,
    // coming back here afterwards.
    reconnect() {
      logout()
      this.$router.push({ path: '/login', query: { redirect: '/admin' } })
    },
    // ---- users directory: sort / search / paginate ----
    toggleSort(key) {
      if (this.sortColumn === key) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = key
        // Sensible default direction per column: newest-first for dates,
        // admins-first for role, A→Z for text.
        this.sortDir = key === 'JOINED' || key === 'ADMIN' ? 'desc' : 'asc'
      }
      this.userOffset = 0 // sorting resets to the first page
    },
    sortArrow(key) {
      if (this.sortColumn !== key) return '↕'
      return this.sortDir === 'asc' ? '↑' : '↓'
    },
    onSearchInput() {
      // Debounce so we don't fire a query on every keystroke; reset to page 1.
      if (this.searchDebounce) clearTimeout(this.searchDebounce)
      this.searchDebounce = setTimeout(() => {
        this.userOffset = 0
      }, 300)
    },
    nextPage() {
      if (this.userOffset + this.userPageSize < this.usersPage.totalCount) {
        this.userOffset += this.userPageSize
      }
    },
    prevPage() {
      this.userOffset = Math.max(0, this.userOffset - this.userPageSize)
    },
    // ---- draft/form helpers ----
    ensureDraft(id) {
      if (!this.drafts[id]) {
        this.drafts[id] = { mode: null, rewrite: '', reason: '', reasonChoice: '', reasonCustom: '' }
      }
      return this.drafts[id]
    },
    openForm(id, mode) {
      return this.drafts[id] && this.drafts[id].mode === mode
    },
    closeForm(id) {
      const d = this.ensureDraft(id)
      d.mode = null
    },
    startRewrite(entry) {
      const d = this.ensureDraft(entry.joke.id)
      const proposed = (entry.reports || []).map((r) => r.proposedRewrite).find(Boolean)
      d.rewrite = proposed || entry.joke.text
      d.mode = 'rewrite'
    },
    startArchive(id) {
      const d = this.ensureDraft(id)
      d.reason = ''
      d.mode = 'archive'
    },
    startRemove(id) {
      const d = this.ensureDraft(id)
      d.reasonChoice = ''
      d.reasonCustom = ''
      d.mode = 'remove'
    },
    resolvedReason(id) {
      const d = this.drafts[id]
      if (!d) return ''
      if (d.reasonChoice === 'Autre') return d.reasonCustom.trim()
      return d.reasonChoice
    },
    // ---- toast ----
    notify(text, type = 'success') {
      this.toast = { text, type }
      if (this.toastTimer) clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => { this.toast = null }, 4000)
    },
    gqlError(e) {
      const msg = e?.graphQLErrors?.[0]?.message || e?.message || 'Une erreur est survenue.'
      this.notify(msg, 'error')
    },
    // ---- joke actions ----
    async doRewrite(entry) {
      const id = entry.joke.id
      const newText = this.drafts[id].rewrite.trim()
      this.busyId = id
      try {
        await this.$apollo.mutate({
          mutation: REWRITE_JOKE_MUTATION,
          variables: { jokeId: parseInt(id, 10), newText },
        })
        this.notify('Blague réécrite ✏️')
        await this.refetchJokes()
      } catch (e) { this.gqlError(e) } finally { this.busyId = null }
    },
    async doArchive(entry) {
      const id = entry.joke.id
      const reason = this.drafts[id].reason.trim()
      this.busyId = id
      try {
        await this.$apollo.mutate({
          mutation: ARCHIVE_JOKE_MUTATION,
          variables: { jokeId: parseInt(id, 10), reason },
        })
        this.notify('Blague archivée 🗄️')
        await this.refetchJokes()
      } catch (e) { this.gqlError(e) } finally { this.busyId = null }
    },
    async doDismiss(id) {
      this.busyId = id
      try {
        await this.$apollo.mutate({
          mutation: DISMISS_REPORTS_MUTATION,
          variables: { jokeId: parseInt(id, 10) },
        })
        this.notify('Signalements ignorés ✅')
        await this.refetchJokes()
      } catch (e) { this.gqlError(e) } finally { this.busyId = null }
    },
    // ---- comment actions ----
    async doRemoveComment(entry) {
      const id = entry.comment.id
      const reason = this.resolvedReason(id)
      if (!reason) return
      this.busyId = id
      try {
        await this.$apollo.mutate({
          mutation: REMOVE_COMMENT_MUTATION,
          variables: { commentId: parseInt(id, 10), reason },
        })
        this.notify('Commentaire supprimé 🗑️')
        await this.refetchComments()
      } catch (e) { this.gqlError(e) } finally { this.busyId = null }
    },
    async doDismissComment(id) {
      this.busyId = id
      try {
        await this.$apollo.mutate({
          mutation: DISMISS_REPORTS_MUTATION,
          variables: { commentId: parseInt(id, 10) },
        })
        this.notify('Signalements ignorés ✅')
        await this.refetchComments()
      } catch (e) { this.gqlError(e) } finally { this.busyId = null }
    },
    // ---- refetch ----
    async refetchJokes() {
      this.drafts = {}
      await this.$apollo.queries.reportedJokes.refetch()
    },
    async refetchComments() {
      this.drafts = {}
      await this.$apollo.queries.reportedComments.refetch()
    },
    formatDate(iso) {
      if (!iso) return '—'
      try {
        return new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium' }).format(new Date(iso))
      } catch { return iso }
    },
  },
  beforeUnmount() {
    if (this.toastTimer) clearTimeout(this.toastTimer)
    if (this.searchDebounce) clearTimeout(this.searchDebounce)
  },
}
</script>

<style scoped>
.admin { padding: 2.5rem 1rem 4rem; }
.admin__head { text-align: center; margin-bottom: 1.6rem; }
.admin__head h1 { font-size: clamp(2rem, 5vw, 3rem); }
.admin__sub { color: var(--text-soft); margin: 0; }

/* Tabs (reused style from SortTabs) */
.tabs {
  display: flex;
  gap: 4px;
  padding: 5px;
  margin: 0 auto 1.8rem;
  width: fit-content;
  max-width: 100%;
  background: var(--cream);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-pill);
  box-shadow: var(--shadow-sm);
  flex-wrap: wrap;
  justify-content: center;
}
.tabs__tab {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.92rem;
  padding: 8px 18px;
  border: none;
  background: transparent;
  color: var(--ink-700);
  border-radius: var(--r-pill);
  cursor: pointer;
  transition: all 0.2s var(--ease-out);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
}
.tabs__emoji { font-size: 0.95rem; }
.tabs__tab:hover { background: var(--banana-100); }
.tabs__tab.active { background: var(--choco); color: var(--banana-400); transform: translateY(-1px); }

@media (max-width: 620px) {
  .tabs { flex-wrap: nowrap; overflow-x: auto; justify-content: flex-start; scrollbar-width: none; }
  .tabs::-webkit-scrollbar { display: none; }
  .tabs__tab { flex: 0 0 auto; padding: 9px 14px; font-size: 0.85rem; }
}

.panel { display: flex; flex-direction: column; gap: 18px; }

.state { text-align: center; color: var(--text-soft); padding: 2rem 0; }
.state--empty { font-family: var(--font-display); font-weight: 700; font-size: 1.1rem; color: var(--ink-700); }

/* Moderation card */
.mod-card {
  background: var(--surface);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-lg);
  box-shadow: var(--shadow-md);
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.mod-card__top { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.mod-card__author { font-size: 0.82rem; color: var(--text-soft); font-weight: 600; }
.mod-card__id {
  margin-left: auto;
  font-family: var(--font-display);
  font-weight: 800;
  color: var(--banana-700);
  font-size: 0.9rem;
}
.mod-card__text {
  font-size: 1.1rem;
  line-height: 1.55;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: var(--ink-900);
  margin: 0;
}
.mod-card__text--comment {
  font-size: 1rem;
  background: var(--banana-50);
  border-left: 4px solid var(--banana-500);
  padding: 10px 14px;
  border-radius: var(--r-sm);
}

.badge {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.76rem;
  padding: 3px 10px;
  border-radius: var(--r-pill);
  border: 1.5px solid var(--border-c);
  white-space: nowrap;
}
.badge--count { background: var(--banana-300); color: var(--ink-900); }
.badge--removed { background: var(--bruise); color: #fff; border-color: var(--bruise); }
.badge--super { background: var(--grape); color: #fff; border-color: var(--grape); }
.badge--staff { background: var(--banana-300); color: var(--ink-900); }
.badge--user { background: var(--cream); color: var(--ink-600); }

/* Reports list */
.reports { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
.report {
  border-top: 1.5px dashed var(--line);
  padding-top: 10px;
}
.report__msg { margin: 0; font-size: 0.92rem; color: var(--ink-700); font-style: italic; }
.report__rewrite {
  margin-top: 8px;
  background: var(--banana-100);
  border: 1.5px solid var(--banana-500);
  border-radius: var(--r-sm);
  padding: 8px 12px;
}
.report__rewrite-tag {
  display: inline-block;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--banana-800);
  margin-bottom: 4px;
}
.report__rewrite p { margin: 0; color: var(--ink-900); font-weight: 600; }

/* Actions & forms */
.mod-card__actions { display: flex; gap: 10px; flex-wrap: wrap; padding-top: 4px; }
.inline-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  background: var(--banana-50);
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-md);
}
.inline-form__label {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.85rem;
  color: var(--ink-800);
}
.inline-form__actions { display: flex; gap: 10px; flex-wrap: wrap; }
.field {
  font-family: var(--font-body);
  font-size: 1rem;
  padding: 10px 12px;
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-sm);
  background: var(--cream);
  color: var(--ink-900);
  width: 100%;
  resize: vertical;
}
.field:focus-visible { outline: 3px solid var(--banana-500); outline-offset: 1px; }

.reason-list { display: flex; flex-wrap: wrap; gap: 8px; }
.reason-chip {
  font-family: var(--font-body);
  font-weight: 600;
  font-size: 0.85rem;
  padding: 6px 12px;
  border: 1.5px solid var(--border-c);
  background: var(--cream);
  color: var(--ink-800);
  border-radius: var(--r-pill);
  cursor: pointer;
  transition: all 0.15s var(--ease-out);
}
.reason-chip:hover { background: var(--banana-100); }
.reason-chip.active { background: var(--choco); color: var(--banana-400); }

/* Users directory: search bar + count */
.users-bar {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}
.users-search {
  position: relative;
  flex: 1 1 260px;
  display: flex;
  align-items: center;
}
.users-search__icon {
  position: absolute;
  left: 14px;
  font-size: 0.95rem;
  pointer-events: none;
  opacity: 0.7;
}
.users-search__field {
  font-family: var(--font-body);
  font-size: 1rem;
  width: 100%;
  padding: 11px 14px 11px 40px;
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-pill);
  background: var(--cream);
  color: var(--ink-900);
}
.users-search__field:focus-visible { outline: 3px solid var(--banana-500); outline-offset: 1px; }
.users-count {
  margin: 0;
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.9rem;
  color: var(--ink-700);
  white-space: nowrap;
}

/* Users table */
.users-table {
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-lg);
  overflow: hidden;
  background: var(--surface);
  box-shadow: var(--shadow-md);
}
.users-row {
  display: grid;
  grid-template-columns: 1.2fr 1.6fr 1fr 1fr;
  gap: 12px;
  padding: 12px 18px;
  align-items: center;
  border-top: 1.5px solid var(--line);
  font-size: 0.9rem;
}
.users-row:first-child { border-top: none; }
.users-row--head {
  background: var(--banana-300);
  padding: 0;
}
/* Sortable header cells are buttons */
.users-th {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--ink-900);
  background: transparent;
  border: none;
  padding: 12px 8px;
  text-align: left;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.users-th:first-child { padding-left: 18px; }
.users-th--sortable { cursor: pointer; }
.users-th--sortable:hover { background: var(--banana-400); }
.users-th--active { background: var(--banana-400); }
.users-th__arrow { font-size: 0.85rem; opacity: 0.85; }
.users-th:disabled { cursor: default; }

.users-name { font-weight: 700; color: var(--ink-900); }
.users-email { color: var(--ink-700); overflow-wrap: anywhere; }

/* Pagination */
.users-pager {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;
}
.users-pager__info {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.88rem;
  color: var(--ink-700);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

@media (max-width: 620px) {
  /* Header row hidden; each data row becomes a stacked card with labels. */
  .users-row--head { display: none; }
  .users-row {
    grid-template-columns: 1fr;
    gap: 6px;
    padding: 14px 16px;
  }
  .users-row > span { display: flex; justify-content: space-between; gap: 10px; }
  .users-row > span::before {
    content: attr(data-label);
    font-family: var(--font-display);
    font-weight: 800;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--ink-500);
    flex-shrink: 0;
  }
  .users-name { font-size: 1.02rem; }
  .users-email { text-align: right; }
}

/* Toast */
.toast {
  position: fixed;
  left: 50%;
  bottom: 28px;
  transform: translateX(-50%);
  z-index: 100;
  display: flex;
  align-items: center;
  gap: 14px;
  font-family: var(--font-display);
  font-weight: 700;
  padding: 12px 18px;
  border: var(--border-w) solid var(--border-c);
  border-radius: var(--r-pill);
  box-shadow: var(--shadow-pop);
  max-width: 90vw;
}
.toast--success { background: var(--banana-400); color: var(--ink-900); }
.toast--error { background: var(--bruise); color: #fff; }
.toast__close { background: none; border: none; cursor: pointer; color: inherit; font-size: 0.9rem; }
.toast-enter-active, .toast-leave-active { transition: all 0.3s var(--ease-bounce); }
.toast-enter-from, .toast-leave-to { opacity: 0; transform: translate(-50%, 16px); }
</style>
